<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { twMerge } from 'tailwind-merge'
import ArrowRight from '@/components/icons/ArrowRight.vue'
import { useElementSize } from '@vueuse/core'

const props = withDefaults(defineProps<{
  contentClass?: string
  gap?: number
  autoScroll?: boolean
  autoScrollInterval?: number
  visibleCount?: number
}>(), {
  contentClass: '',
  gap: 0,
  autoScroll: false,
  autoScrollInterval: 6000,
  visibleCount: undefined,
})

const listRef = ref<HTMLElement | null>(null)
const swipeAreaRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
const resizeObserver = ref<ResizeObserver | null>(null)

// --- CONFIGURATION ---
const fixedArrowSize = 24
const minGap = 0
const arrowsTotalWidth = fixedArrowSize * 2
const SWIPE_THRESHOLD = 50 // Minimum horizontal distance in pixels to register a swipe

const { width: containerWidth } = useElementSize(containerRef)

// --- STATE ---
const itemWidthsWithGap = ref<number[]>([])
const firstVisible = ref(0) // Start index of the visible buttons

// Reset firstVisible when the number of buttons changes
watch(
  () => itemWidthsWithGap.value.length,
  (newLength, oldLength) => {
    if (newLength !== oldLength) {
      firstVisible.value = 0
    }
  }
)

// --- SWIPE STATE ---
const touchStartX = ref(0)
const touchCurrentX = ref(0) // Tracks current position for real-time drag
const isSwiping = ref(false)

// --- AUTO-SCROLL STATE ---
const autoScrollTimer = ref<ReturnType<typeof setInterval> | null>(null)
const resumeTimer = ref<ReturnType<typeof setTimeout> | null>(null)

// --- CORE LOGIC FUNCTIONS ---
/**
 * 1. Measures the width of all slotted children and adds the 10px minimum gap.
 */
function measureButtonWidths() {
  const listEl = listRef.value
  if (!listEl) {
    itemWidthsWithGap.value = []
    return
  }

  const children = Array.from(listEl.children) as HTMLElement[]
  const widths: number[] = []

  for (const el of children) {
    // Measure actual button width and add the 10px minimum gap
    const w = el.offsetWidth
    if (w > 0) {
      widths.push(w + minGap)
    }
  }
  itemWidthsWithGap.value = widths
}

/**
 * 2. Calculates how many buttons (N) fit into the available screen width.
 *    Uses visibleCount prop if provided, otherwise auto-calculates.
 */
const calculatedVisibleCount = computed(() => {
  const maxCount = props.visibleCount !== undefined
    ? Math.min(props.visibleCount, itemWidthsWithGap.value.length)
    : itemWidthsWithGap.value.length

  let availableWidth = containerWidth.value - arrowsTotalWidth
  let count = 0

  if (itemWidthsWithGap.value.length === 0 || availableWidth <= 0) {
    return 0
  }

  for (const itemWidthWithGap of itemWidthsWithGap.value) {
    if (count >= maxCount) break
    if (availableWidth < itemWidthWithGap) break
    availableWidth -= itemWidthWithGap
    count++
  }
  return count
})

// --- RENDERING & SCROLL LOGIC ---
const viewportWidth = computed(() => {
  const N = calculatedVisibleCount.value
  if (N === 0) return 0

  const itemsWidthSum = itemWidthsWithGap.value
    .slice(firstVisible.value, firstVisible.value + N)
    .reduce((sum, wPlusGap) => sum + (wPlusGap - minGap), 0)

  const gapsTotalWidth = (N > 0 ? N - 1 : 0) * minGap

  return itemsWidthSum + gapsTotalWidth
})

const lastVisible = computed(() =>
  Math.min(firstVisible.value + calculatedVisibleCount.value - 1, Math.max(0, itemWidthsWithGap.value.length - 1)),
)
const canScrollBackward = computed(() => firstVisible.value > 0)
const canScrollForward = computed(() => lastVisible.value < itemWidthsWithGap.value.length - 1)

// Calculates the base offset from the firstVisible index
const baseOffset = computed(() => {
  let offset = 0
  for (let i = 0; i < firstVisible.value; i++) {
    offset += itemWidthsWithGap.value[i]
  }
  return offset
})

// Applies both the base offset (from firstVisible index) and the temporary swipe drag offset
const transformOffset = computed(() => {
  const dragDelta = isSwiping.value ? touchCurrentX.value - touchStartX.value : 0
  const totalOffset = baseOffset.value - dragDelta

  return `translateX(-${totalOffset}px)`
})

// --- NAVIGATION HANDLERS ---
function stepForward() {
  if (!canScrollForward.value) return
  firstVisible.value = Math.min(
    firstVisible.value + 1,
    Math.max(0, itemWidthsWithGap.value.length - calculatedVisibleCount.value),
  )
}

function stepBackward() {
  if (!canScrollBackward.value) return
  firstVisible.value = Math.max(0, firstVisible.value - 1)
}

function startAutoScroll() {
  if (!props.autoScroll) return
  stopAutoScroll()
  autoScrollTimer.value = setInterval(() => {
    if (canScrollForward.value) {
      stepForward()
    } else {
      firstVisible.value = 0
    }
  }, props.autoScrollInterval)
}

function stopAutoScroll() {
  if (autoScrollTimer.value) {
    clearInterval(autoScrollTimer.value)
    autoScrollTimer.value = null
  }
}

function pauseAndScheduleResume() {
  stopAutoScroll()
  if (resumeTimer.value) clearTimeout(resumeTimer.value)
  resumeTimer.value = setTimeout(() => {
    if (canScrollForward.value) {
      stepForward()
    } else {
      firstVisible.value = 0
    }
    startAutoScroll()
  }, props.autoScrollInterval)
}

// --- SWIPE HANDLERS ---
function handleTouchStart(event: TouchEvent) {
  if (event.touches.length !== 1) return

  if (listRef.value) {
    listRef.value.style.transition = 'none'
  }

  touchStartX.value = event.touches[0].clientX
  touchCurrentX.value = event.touches[0].clientX
  isSwiping.value = true
}

function handleTouchMove(event: TouchEvent) {
  if (!isSwiping.value) return

  event.preventDefault()

  const deltaX = event.touches[0].clientX - touchStartX.value

  // Get the width of the target item for the step
  let stepWidth = 0

  if (deltaX < 0 && canScrollForward.value) {
    // Swiping LEFT (forward): Get the width of the next item to reveal
    stepWidth = itemWidthsWithGap.value[firstVisible.value + calculatedVisibleCount.value] || 0

    // Constrain deltaX to be between 0 (no move) and -stepWidth
    const constrainedDeltaX = Math.max(deltaX, -stepWidth)
    touchCurrentX.value = touchStartX.value + constrainedDeltaX

  } else if (deltaX > 0 && canScrollBackward.value) {
    // Swiping RIGHT (backward): Get the width of the previous item to hide
    stepWidth = itemWidthsWithGap.value[firstVisible.value - 1] || 0

    // Constrain deltaX to be between 0 (no move) and +stepWidth
    const constrainedDeltaX = Math.min(deltaX, stepWidth)
    touchCurrentX.value = touchStartX.value + constrainedDeltaX

  } else {
    // No valid scroll direction, stop drag or limit to a small "bounce"
    touchCurrentX.value = touchStartX.value + (deltaX * 0.1)
  }
}

function handleTouchEnd() {
  if (!isSwiping.value) return

  const deltaX = touchCurrentX.value - touchStartX.value
  isSwiping.value = false

  if (listRef.value) {
    listRef.value.style.transition = ''
  }

  // CRITICAL: Reset currentX to reset the visual drag offset immediately
  touchCurrentX.value = touchStartX.value

  // Check if the swipe exceeded the threshold
  if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
    if (deltaX < 0 && canScrollForward.value) { // Swipe Left (step forward)
      stepForward()
    } else if (deltaX > 0 && canScrollBackward.value) { // Swipe Right (step backward)
      stepBackward()
    }
  }

  // Reset state for next swipe
  touchStartX.value = 0

  // Pause auto-scroll on any touch interaction and schedule resume
  pauseAndScheduleResume()
}

// --- LIFECYCLE HOOKS ---
function setupSwipeListeners() {
  const el = swipeAreaRef.value
  if (!el) return

  el.addEventListener('touchstart', handleTouchStart, { passive: true })
  el.addEventListener('touchmove', handleTouchMove, { passive: false })
  el.addEventListener('touchend', handleTouchEnd)
}

function cleanupSwipeListeners() {
  const el = swipeAreaRef.value
  if (!el) return

  el.removeEventListener('touchstart', handleTouchStart)
  el.removeEventListener('touchmove', handleTouchMove)
  el.removeEventListener('touchend', handleTouchEnd)
}

onMounted(() => {
  nextTick(measureButtonWidths)
  setupSwipeListeners()

  // Set up ResizeObserver to re-measure when children sizes change
  if (listRef.value) {
    resizeObserver.value = new ResizeObserver(() => {
      measureButtonWidths()
    })
    resizeObserver.value.observe(listRef.value)
  }

  // Start auto-scroll if enabled
  startAutoScroll()
})

onUnmounted(() => {
  cleanupSwipeListeners()
  resizeObserver.value?.disconnect()

  // Cleanup auto-scroll timers
  stopAutoScroll()
  if (resumeTimer.value) clearTimeout(resumeTimer.value)
})
</script>

<template>
  <div ref="containerRef" :class="twMerge('relative flex justify-center select-none w-full h-full overflow-y-visible', contentClass)">
    <div
      class="z-[2] flex justify-center items-center"
    >
      <button
        :class="['flex h-6 w-full items-center justify-center', { 'invisible pointer-events-none': !canScrollBackward }]"
        @click="stepBackward(); pauseAndScheduleResume()"
        aria-label="Scroll left"
        type="button"
      >
        <ArrowRight
          class="min-w-[20px] size-5 rotate-180"
        />
      </button>
    </div>

    <div
      ref="swipeAreaRef"
      class="hide-scrollbar flex overflow-hidden touch-pan-y cursor-grab"
      :style="{ width: `${viewportWidth}px`, height: '100%' }"
    >
      <div
        ref="listRef"
        class="flex flex-row items-start justify-center transition-transform duration-300 will-change-transform h-full mt-2.5"
        :style="{ gap: `${gap}px`, transform: transformOffset }"
      >
        <slot />
      </div>
    </div>

    <div
      class="z-[2] flex justify-center items-center"
    >
      <button
        :class="['flex h-6 w-full items-center justify-center', { 'invisible pointer-events-none': !canScrollForward }]"
        @click="stepForward(); pauseAndScheduleResume()"
        aria-label="Scroll right"
        type="button"
      >
        <ArrowRight
          class="min-w-[20px] size-5"
        />
      </button>
    </div>
  </div>
</template>

<style scoped>
.hide-scrollbar {
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>